// EXPORTACIÓN COMPLETA DE LA CLÍNICA (29-sep-2026; B-989, 3-oct-2026)
//
// Todos los datos y archivos de la clínica en UN ZIP cifrado con contraseña, para llevarlos a otro programa o guardarlos:
// LEEME.pdf (qué es cada fichero y cada columna), una tabla CSV por tipo de dato, el formato canónico de migración, los
// documentos y fotos de cada paciente en su carpeta, todas las tablas tal cual y un RESUMEN con las cifras para comprobar
// al otro lado. La prepara el servidor en segundo plano (backend/services/exportacionCompleta.js).
//
// LA PIDE LA ADMINISTRACIÓN FIRMÁNDOLA: su nombre, la declaración y su contraseña de Biango (queda en el Registro de
// actividad). La contraseña del ZIP se enseña UNA vez, al pedirla: no se guarda en ningún sitio. La descarga es un enlace
// de pocos minutos y de un solo uso que el navegador baja directo a disco (puede pesar gigas).
//
// Va en Ajustes → «Copias y portabilidad» y en el aviso de la cuenta en pausa (AvisoImpago). El soporte de Biango la
// prepara desde Superadmin → Clínicas → Licencia (pages/superadmin/ExportacionClinicaLicencia.jsx), con las mismas piezas
// de aquí: ListaExportaciones y ContrasenaUnaVez.
//
// B-2143 (25-oct-2026) · LOS ARCHIVOS EN TROZOS. Al pedirla se elige cómo van los archivos: en trozos de 1, 2 (de fábrica)
// o 4 GB, o todo en un solo fichero. En trozos, la lista enseña cada parte (los datos y cada trozo de los archivos) con su
// tamaño, su huella y su propio botón de descarga, y marca las que ya se han bajado enteras.
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { msgError } from '../utils/errores'
import { useToast } from '../context/ToastContext'
import { confirmar } from '../context/DialogosContext'
import { Boton } from './ui'
// L21 (4-oct-2026) · la contraseña que el llavero del iPhone sabe ofrecer, y leída del formulario (utils/reautenticar).
import { usuarioDeLaSesion, contrasenaDelFormulario } from '../utils/reautenticar'
import { tamanoExportacion, textoProgreso, ESTADOS_EXPORTACION, COMO_SE_ABRE, lineaDePeticion, lineaDeCifras,
  OPCIONES_TROZOS, TXT_TROZOS, TXT_TROZOS_NO_DISPONIBLE, trozoGbDe, lineaDeParte } from '../utils/exportacionTextos'
import { t, tx, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export { tamanoExportacion, COMO_SE_ABRE } from '../utils/exportacionTextos'

// LA CONTRASEÑA DEL ZIP, UNA SOLA VEZ: para copiarla o apuntarla. Al cerrar, no se vuelve a ver.
export function ContrasenaUnaVez({ contrasena, onCerrar, paraQuien = t('components.ExportacionCompleta.quien_vaya_a_abrir_el', 'quien vaya a abrir el fichero') }) {
  const toast = useToast()
  const [copiada, setCopiada] = useState(false)
  if (!contrasena) return null
  const copiar = async () => {
    try { await navigator.clipboard.writeText(contrasena); setCopiada(true); toast.success(t('components.ExportacionCompleta.contrasena_copiada', 'Contraseña copiada')) }
    catch (_) { toast.error(t('components.ExportacionCompleta.no_se_ha_podido_copiar', 'No se ha podido copiar: apúntala a mano')) }
  }
  const cerrar = async () => {
    const ok = await confirmar({
      titulo: t('components.ExportacionCompleta.la_has_apuntado', '¿La has apuntado?'),
      mensaje: t('components.ExportacionCompleta.despues_de_cerrar_esto_no', 'Después de cerrar esto no se vuelve a ver: sin ella, el fichero no se puede abrir y habría que preparar otra exportación.'),
      textoConfirmar: t('components.ExportacionCompleta.si_la_tengo', 'Sí, la tengo'), tipo: 'warning',
    })
    if (ok) onCerrar()
  }
  return (
    <div role="alert" data-exportacion-contrasena="" style={{ border: '2px solid var(--warning, #d97706)', borderRadius: 10, background: 'var(--surface)', padding: '12px 14px' }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 6 }}>{t('components.ExportacionCompleta.apunta_la_contrasena_del_fichero', 'Apunta la contraseña del fichero: solo se enseña ahora')}</div>
      <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 19, fontWeight: 700, letterSpacing: 1, wordBreak: 'break-all', userSelect: 'all', margin: '4px 0 8px' }}>
        {contrasena}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 10 }}>
        {tx('components.ExportacionCompleta.biango_no_la_guarda_dasela', 'Biango no la guarda. Dásela a {{paraQuien}} por un camino distinto del fichero (por teléfono o en persona, nunca en el mismo correo). {{COMO_SE_ABRE}}', { paraQuien, COMO_SE_ABRE: tDato(COMO_SE_ABRE) })}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Boton variante="neutro" icon="copy" onClick={copiar}>{copiada ? t('components.ExportacionCompleta.copiada', 'Copiada') : t('components.ExportacionCompleta.copiar', 'Copiar')}</Boton>
        <Boton variante="primario" onClick={cerrar}>{t('components.ExportacionCompleta.ya_la_he_apuntado', 'Ya la he apuntado')}</Boton>
      </div>
    </div>
  )
}

// B-2143 · LAS PARTES DE UNA EXPORTACIÓN EN TROZOS: cada una con su tamaño, su huella y su botón. `bajando`: «id:parte».
export function PartesExportacion({ e, onDescargar, bajando, desactivado = false, porQue }) {
  if (!e || !Array.isArray(e.partes) || !e.partes.length) return null
  return (
    <div data-exportacion-partes="" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {tx('components.ExportacionCompleta.en_partes_en_total_baja', 'En {{length}} partes ({{tamanoExportacion}} en total). Baja la primera (los datos) y cada trozo de los archivos: cada uno se abre solo con la misma contraseña, y si una descarga se corta el navegador la sigue donde se quedó.', { length: e.partes.length, tamanoExportacion: tamanoExportacion(e.bytes_en_total) })}
      </div>
      {e.partes.map((p) => {
        const k = e.id + ':' + p.parte
        return (
          <div key={p.parte} data-exportacion-parte={p.parte} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 6 }}>
            <span style={{ fontSize: 12.5, flex: '1 1 220px', minWidth: 0 }}>
              {lineaDeParte(p)}
              {p.descargada && <span style={{ color: 'var(--success, #059669)', fontWeight: 600 }}>{t('components.ExportacionCompleta.descargada', ' · descargada')}</span>}
              {p.sha256 && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{tx('components.ExportacionCompleta.huella_sha_256', 'Huella SHA-256: {{sha256}}', { sha256: p.sha256 })}</span>}
            </span>
            <Boton variante={p.descargada ? 'neutro' : 'primario'} icon="download" disabled={desactivado || bajando === k} title={desactivado ? porQue : undefined} onClick={() => onDescargar(e, p.parte)}>
              {bajando === k ? t('components.ExportacionCompleta.preparando', 'Preparando…') : t('components.ExportacionCompleta.descargar', 'Descargar')}
            </Boton>
          </div>
        )
      })}
    </div>
  )
}

// LA LISTA DE EXPORTACIONES DE LA CLÍNICA: estado, progreso, cifras y descarga.
export function ListaExportaciones({ lista, onDescargar, onBorrar, bajando }) {
  if (!lista || !lista.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {lista.map((e) => {
        const est = ESTADOS_EXPORTACION[e.estado] || { txt: e.estado, color: 'var(--text-muted)' }
        const r = e.resumen || {}
        return (
          <div key={e.id} data-exportacion={e.estado} style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', padding: '10px 12px' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: est.color }}>{est.txt}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {lineaDePeticion(e)}
              </span>
            </div>
            {(e.estado === 'pendiente' || e.estado === 'generando') && (
              <div style={{ fontSize: 12.5, marginTop: 4 }}>{textoProgreso(e.progreso)}</div>
            )}
            {e.estado === 'fallida' && e.error && <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 4 }}>{e.error}</div>}
            {e.estado === 'lista' && (
              <>
                <div style={{ fontSize: 12.5, marginTop: 4, color: 'var(--text-secondary)' }}>{lineaDeCifras(e)}</div>
                {Array.isArray(r.entidades) && r.entidades.length > 0 && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)' }}>{t('components.ExportacionCompleta.cifras_para_comprobar_al_otro', 'Cifras para comprobar al otro lado')}</summary>
                    <div style={{ fontSize: 12, lineHeight: 1.6, marginTop: 4 }}>
                      {r.entidades.filter((x) => x.registros > 0).map((x) => (
                        <div key={x.entidad}>
                          {x.etiqueta}: <b>{Number(x.registros).toLocaleString('es-ES')}</b>
                          {Object.entries(x.sumas || {}).map(([k, v]) => <span key={k} style={{ color: 'var(--text-muted)' }}> · {k.replace(/_/g, ' ')} {String(v).replace('.', ',')} €</span>)}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {r.incompleta && (
                  <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 4 }}>
                    <b>{t('components.ExportacionCompleta.incompleta', 'Incompleta:')}</b>{tx('components.ExportacionCompleta.avisa_a_soporte_antes_de', ' avisa a soporte antes de darla por buena.{{x}}', { x: (r.motivos || []).length > 0 && (
                      <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>{r.motivos.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    ) })}
                  </div>
                )}
                {r.archivos_que_faltan > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {tx('components.ExportacionCompleta.filas_nombran_un_archivo_que', '{{toLocaleString}} filas nombran un archivo que ya no está en el servidor: el informe de la exportación dice cuáles.', { toLocaleString: Number(r.archivos_que_faltan).toLocaleString('es-ES') })}
                  </div>
                )}
                {!e.partes && e.sha256 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, wordBreak: 'break-all' }}>{tx('components.ExportacionCompleta.huella_sha_256_del_fichero', 'Huella SHA-256 del fichero: {{sha256}}', { sha256: e.sha256 })}</div>}
                <PartesExportacion e={e} onDescargar={onDescargar} bajando={bajando} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {!e.partes && (
                    <Boton variante="primario" icon="download" disabled={bajando === e.id + ':completa'} onClick={() => onDescargar(e, 'completa')}>
                      {bajando === e.id + ':completa' ? t('components.ExportacionCompleta.preparando', 'Preparando…') : t('components.ExportacionCompleta.descargar_2', 'Descargar ({{tamanoExportacion}})', { tamanoExportacion: tamanoExportacion(e.bytes) })}
                    </Boton>
                  )}
                  <Boton variante="neutro" icon="trash" onClick={() => onBorrar(e)} style={{ color: 'var(--danger)' }}>{t('components.ExportacionCompleta.borrar_del_servidor', 'Borrar del servidor')}</Boton>
                </div>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

const inpStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }

export default function ExportacionCompleta() {
  const toast = useToast()
  const qc = useQueryClient()
  const [conArchivos, setConArchivos] = useState(true)
  const [trozo, setTrozo] = useState('2')
  const [abierta, setAbierta] = useState(false)
  const [nombre, setNombre] = useState('')
  const [acepto, setAcepto] = useState(false)
  const [password, setPassword] = useState('')
  const [pidiendo, setPidiendo] = useState(false)
  const [contrasena, setContrasena] = useState('')
  const [bajando, setBajando] = useState(null)
  const { data, isLoading, error } = useQuery({
    queryKey: ['exportacion-completa'],
    queryFn: () => api.get('/portabilidad/completa').then((r) => r.data),
    // Mientras haya una preparándose, se mira cada 3 segundos; si no, nada.
    refetchInterval: (q) => {
      const l = (q && q.state && q.state.data && q.state.data.exportaciones) || []
      return l.some((e) => e.estado === 'pendiente' || e.estado === 'generando') ? 3000 : false
    },
  })
  const { data: firma } = useQuery({
    queryKey: ['exportacion-completa-firma'],
    queryFn: () => api.get('/portabilidad/completa/firma').then((r) => r.data),
    enabled: abierta,
  })
  const lista = (data && data.exportaciones) || []
  const enMarcha = lista.some((e) => e.estado === 'pendiente' || e.estado === 'generando')
  const refrescar = () => qc.invalidateQueries({ queryKey: ['exportacion-completa'] })
  // En trozos solo si el servidor ya tiene su tabla de enlaces (B-2143); si no, se prepara en un solo fichero.
  const trozosDisponibles = !!(data && data.trozos && data.trozos.disponible)
  // La contraseña de la cuenta no se queda en la pantalla después de usarla.
  useEffect(() => { if (!abierta) setPassword('') }, [abierta])

  const declaracion = firma && firma.plantilla ? firma.plantilla.split('{{nombre}}').join(nombre.trim() || '[tu nombre y apellidos]') : ''
  // L21 · EL BOTÓN NO SE APAGA POR LA CONTRASEÑA VACÍA: con el llavero del iPhone el estado puede estar vacío con la
  // casilla llena, y el botón se quedaba gris para siempre. Se lee al enviar; si de verdad falta, se dice.
  const puedeFirmar = nombre.trim().length >= 3 && acepto && !pidiendo

  const pedir = async (ev) => {
    if (ev) ev.preventDefault()
    if (!puedeFirmar) return
    const clave = contrasenaDelFormulario(ev && ev.currentTarget, password)
    if (!clave) return toast.error(t('components.ExportacionCompleta.escribe_tu_contrasena_de_biango', 'Escribe tu contraseña de Biango para confirmar que eres tú'))
    setPidiendo(true)
    try {
      const r = await api.post('/portabilidad/completa', { archivos: conArchivos, nombre: nombre.trim(), acepto: true, password: clave,
        trozo_gb: conArchivos && trozosDisponibles ? trozoGbDe(trozo) : null })
      setContrasena(r.data.contrasena)
      setAbierta(false); setAcepto(false); setPassword('')
      toast.success(t('components.ExportacionCompleta.preparando_la_exportacion_puedes_seguir', 'Preparando la exportación. Puedes seguir trabajando.'))
      refrescar()
    } catch (e) { toast.error(msgError(e, t('components.ExportacionCompleta.no_se_ha_podido_pedir', 'No se ha podido pedir la exportación'))) }
    setPidiendo(false)
  }

  const descargar = async (exp, parte = 'completa') => {
    setBajando(exp.id + ':' + parte)
    try {
      const r = await api.post(`/portabilidad/completa/${exp.id}/descargar`, { parte })
      // El enlace dura unos minutos, sirve para una descarga y el navegador lo baja directo a disco.
      window.location.href = r.data.url
      setTimeout(refrescar, 2000)
    } catch (e) { toast.error(msgError(e, t('components.ExportacionCompleta.no_se_ha_podido_preparar', 'No se ha podido preparar la descarga'))) }
    setBajando(null)
  }

  const borrar = async (exp) => {
    const ok = await confirmar({
      titulo: t('components.ExportacionCompleta.borrar_la_exportacion_del_servidor', 'Borrar la exportación del servidor'),
      mensaje: t('components.ExportacionCompleta.se_borra_ya_del_servidor', 'Se borra ya del servidor. Si todavía no la has descargado, tendrás que preparar otra.'),
      textoConfirmar: t('components.ExportacionCompleta.borrar', 'Borrar'),
    })
    if (!ok) return
    try { await api.delete(`/portabilidad/completa/${exp.id}`); toast.success(t('components.ExportacionCompleta.borrada_del_servidor', 'Borrada del servidor')); refrescar() }
    catch (e) { toast.error(msgError(e, t('components.ExportacionCompleta.no_se_ha_podido_borrar', 'No se ha podido borrar'))) }
  }

  return (
    <div data-exportacion-completa="" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4 }}>{t('components.ExportacionCompleta.exportacion_completa', 'Exportación completa')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          {t('components.ExportacionCompleta.todos_los_datos_y_archivos', 'Todos los datos y archivos de la clínica en un fichero ZIP cifrado con contraseña (los archivos, si quieres, en trozos más pequeños), para llevarlos a otro programa o guardarlos: tablas en CSV que abre Excel (pacientes, citas, tratamientos, presupuestos, cobros, facturas…), el formato de migración que entienden otros programas, los documentos y fotos de cada paciente en su carpeta, y un índice (LEEME.pdf) y un resumen con las cifras para comprobar que ha llegado todo. Tus datos son tuyos.')}
        </div>
      </div>

      <ContrasenaUnaVez contrasena={contrasena} onCerrar={() => setContrasena('')} />

      {data && data.disponible === false && (
        <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>{t('components.ExportacionCompleta.la_exportacion_completa_todavia_no', 'La exportación completa todavía no está preparada en este servidor. Avisa a soporte de Biango.')}</div>
      )}

      {!abierta && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Boton variante="primario" icon="download" onClick={() => setAbierta(true)} disabled={enMarcha || (data && data.disponible === false)}>
            {enMarcha ? t('components.ExportacionCompleta.preparandose', 'Preparándose…') : t('components.ExportacionCompleta.pedir_la_exportacion_completa', 'Pedir la exportación completa')}
          </Boton>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {tx('components.ExportacionCompleta.se_queda_en_el_servidor', 'Se queda en el servidor {{x}} días, o hasta el día siguiente de descargarla, y después se borra sola.', { x: (data && data.dias) || 7 })}
          </span>
        </div>
      )}

      {abierta && (
        <form onSubmit={pedir} data-exportacion-firma="" style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface-2, var(--surface))', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{t('components.ExportacionCompleta.firmar_la_peticion', 'Firmar la petición')}</div>
          {firma && firma.de_plataforma && (
            <div style={{ fontSize: 12.5, color: 'var(--warning)' }}>{t('components.ExportacionCompleta.estas_dentro_de_la_clinica', 'Estás dentro de la clínica como soporte de Biango: prepárala desde Superadmin, en la ficha de la clínica, pestaña «Datos».')}</div>
          )}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={conArchivos} onChange={(e) => setConArchivos(e.target.checked)} style={{ marginTop: 2 }} />
            <span>{t('components.ExportacionCompleta.incluir_los_archivos_radiografias_fotos', 'Incluir los archivos (radiografías, fotos, documentos y audios). ')}<span style={{ color: 'var(--text-muted)' }}>{t('components.ExportacionCompleta.puede_pesar_varios_gigas_y', 'Puede pesar varios gigas y tardar un rato.')}</span></span>
          </label>
          {conArchivos && (
            <div data-exportacion-trozos="">
              <label htmlFor="exp-trozos" style={{ fontSize: 12, fontWeight: 600 }}>{t('components.ExportacionCompleta.como_quieres_los_archivos', 'Cómo quieres los archivos')}</label>
              <select id="exp-trozos" value={trozosDisponibles ? trozo : 'uno'} onChange={(e) => setTrozo(e.target.value)} disabled={!trozosDisponibles} style={inpStyle}>
                {OPCIONES_TROZOS.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
              </select>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 4 }}>{trozosDisponibles ? tDato(TXT_TROZOS) : tDato(TXT_TROZOS_NO_DISPONIBLE)}</div>
            </div>
          )}
          <div>
            <label htmlFor="exp-nombre" style={{ fontSize: 12, fontWeight: 600 }}>{t('components.ExportacionCompleta.tu_nombre_y_apellidos', 'Tu nombre y apellidos')}</label>
            <input id="exp-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" style={inpStyle} />
          </div>
          {declaracion && (
            <div style={{ fontSize: 12.5, lineHeight: 1.55, padding: '8px 10px', borderLeft: '3px solid var(--primary)', background: 'var(--surface)' }}>{declaracion}</div>
          )}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} style={{ marginTop: 2 }} />
            <span>{t('components.ExportacionCompleta.firmo_esta_peticion_queda_guardada', 'Firmo esta petición. Queda guardada, con mi nombre, en el Registro de actividad de la clínica.')}</span>
          </label>
          <div>
            <label htmlFor="exp-password" style={{ fontSize: 12, fontWeight: 600 }}>{t('components.ExportacionCompleta.tu_contrasena_de_biango_para', 'Tu contraseña de Biango (para confirmar que eres tú)')}</label>
            {/* L21 · el usuario de esta sesión, oculto: si no, el llavero toma «Tu nombre y apellidos» por el usuario. */}
            <input {...usuarioDeLaSesion()} />
            <input id="exp-password" name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} style={inpStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Boton type="submit" variante="primario" icon="download" disabled={!puedeFirmar}>{pidiendo ? t('components.ExportacionCompleta.pidiendo', 'Pidiendo…') : t('components.ExportacionCompleta.firmar_y_preparar', 'Firmar y preparar')}</Boton>
            <Boton type="button" variante="neutro" onClick={() => setAbierta(false)}>{t('components.ExportacionCompleta.cancelar', 'Cancelar')}</Boton>
          </div>
        </form>
      )}

      {isLoading && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{t('components.ExportacionCompleta.cargando', 'Cargando…')}</div>}
      {error && <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>{msgError(error, t('components.ExportacionCompleta.no_se_han_podido_leer', 'No se han podido leer las exportaciones'))}</div>}
      <ListaExportaciones lista={lista} onDescargar={descargar} onBorrar={borrar} bajando={bajando} />

      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {t('components.ExportacionCompleta.es_informacion_de_salud_de', 'Es información de salud de todos tus pacientes: guárdala en un sitio de acceso restringido y no envíes el fichero y la contraseña juntos. Cada enlace de descarga sirve para una descarga, y cada descarga queda en el Registro de actividad.')}
      </div>
    </div>
  )
}
